"use client";

import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import ProjectMediaViewer from "./ProjectMediaViewer";

export default function ProjectModal({ project, isOpen, onClose, onNext, onPrevious }) {
  const [direction, setDirection] = useState(0);

  // Empêche le scroll du body quand le modal est ouvert et restaure après fermeture
  useEffect(() => {
    // Only run when modal is open
    if (!isOpen) return;

    // Lock scroll but preserve scroll position
    const scrollY = window.scrollY || window.pageYOffset;
    const body = document.body;
    
    // Save original styles
    const prevOverflow = body.style.overflow;
    const prevPaddingRight = body.style.paddingRight;
    
    // Get scrollbar width to prevent layout shift
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    
    // Apply lock
    body.style.overflow = 'hidden';
    body.style.paddingRight = `${scrollbarWidth}px`;

    return () => {
      // Restore original styles
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPaddingRight;
    };
  }, [isOpen]);

  const handleNext = () => {
    setDirection(1);
    onNext();
  };

  const handlePrevious = () => {
    setDirection(-1);
    onPrevious();
  };

  if (!isOpen || !project) return null;

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      x: direction > 0 ? '-100%' : '100%',
      opacity: 0
    })
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/95 md:bg-black/80 md:backdrop-blur-sm z-[9999] flex items-center justify-center md:p-4"
        >
          {/* Desktop Modal */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className="hidden md:block relative z-[10000] bg-gradient-to-br from-gray-900 to-black rounded-3xl shadow-2xl max-w-6xl w-full my-8 overflow-hidden border border-white/10"
          >
            {/* Bouton fermer */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-50 p-2 bg-black/50 hover:bg-black/70 rounded-full transition-colors duration-300"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
              </svg>
            </button>

            {/* Layout horizontal : Image à gauche, Contenu à droite */}
            <div className="flex flex-col lg:flex-row gap-0">
              {/* Partie gauche - Média (Image/Vidéo/Placeholder) */}
              <div className="lg:w-[45%] p-6 lg:p-8 flex items-center justify-center bg-gradient-to-br from-gray-800/30 to-transparent">
                <ProjectMediaViewer project={project} />
              </div>

              {/* Partie droite - Contenu */}
              <div className="lg:w-[55%] p-6 lg:p-8 overflow-y-auto max-h-[85vh] lg:max-h-[600px]">
                <AnimatePresence mode="wait" custom={direction}>
                  <motion.div
                    key={project.id}
                    custom={direction}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                  >
                    {/* Titre et description */}
                    <div className="mb-6">
                      <h2 className="text-3xl lg:text-4xl font-bold text-white mb-3 bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">
                        {project.title}
                      </h2>
                      <p className="text-gray-300 text-base leading-relaxed">
                        {project.fullDescription}
                      </p>
                    </div>

                    {/* Technologies */}
                    <div className="mb-6">
                      <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2 uppercase tracking-wide">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-orange-400">
                          <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
                        </svg>
                        Technologies
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {project.tech.map((tech, index) => (
                          <span
                            key={index}
                            className="px-3 py-1.5 bg-orange-500/20 text-orange-300 text-xs rounded-full border border-orange-500/30 font-medium"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Fonctionnalités */}
                    <div className="mb-8">
                      <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2 uppercase tracking-wide">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-orange-400">
                          <path d="M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z"/><path d="M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4 Z"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/><path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/>
                        </svg>
                        Fonctionnalités clés
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        {project.features.map((feature, index) => (
                          <div key={index} className="flex items-start gap-2 text-gray-300 text-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-orange-400 flex-shrink-0 mt-0.5">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Boutons d'action */}
                    <div className="flex flex-wrap gap-3 pt-6">
                      {project.github && (
                        <motion.a
                          href={project.github}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 sm:flex-none px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all duration-300 border border-white/20 flex items-center justify-center gap-2 font-medium text-sm"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4"/>
                            <path d="M9 18c-4.51 2-5-2-7-2"/>
                          </svg>
                          Code source
                        </motion.a>
                      )}

                      {project.demo && (
                        <motion.a
                          href={project.demo}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 sm:flex-none px-5 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl transition-all duration-300 flex items-center justify-center gap-2 font-medium shadow-lg shadow-orange-500/30 text-sm"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20.2 6 3 11l-.9-2.4c-.3-1.1.3-2.2 1.3-2.5l13.5-4c1.1-.3 2.2.3 2.5 1.3Z"/>
                            <path d="m6.2 5.3 3.1 3.9"/>
                            <path d="m12.4 3.4 3.1 4"/>
                            <path d="M3 11h18v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z"/>
                          </svg>
                          Voir la démo
                        </motion.a>
                      )}
                    </div>
                  </motion.div>
                </AnimatePresence>

                {/* Boutons de navigation Desktop */}
                <div className="flex gap-3 mt-6 pt-6 border-t border-white/10">
                  <motion.button
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePrevious();
                    }}
                    className="flex-1 px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all duration-300 border border-white/20 flex items-center justify-center gap-2 font-medium text-sm"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m15 18-6-6 6-6"/>
                    </svg>
                    Précédent
                  </motion.button>

                  <motion.button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNext();
                    }}
                    className="flex-1 px-5 py-2.5 bg-gradient-to-r from-orange-500/20 to-yellow-500/20 hover:from-orange-500/30 hover:to-yellow-500/30 text-orange-300 rounded-xl transition-all duration-300 border border-orange-500/30 flex items-center justify-center gap-2 font-medium shadow-lg shadow-orange-500/20 text-sm"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Prochain projet
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m9 18 6-6-6-6"/>
                    </svg>
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Mobile: Format plein écran */}
          <div className="md:hidden relative w-full h-full bg-black">
            <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-black via-black/95 to-black/70 backdrop-blur-md p-4 flex items-center justify-between border-b border-white/10 shadow-xl">
              <button onClick={onClose} className="p-2.5 bg-white/10 hover:bg-white/20 rounded-full transition-all duration-200 border border-white/20">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-white">
                  <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
                </svg>
              </button>
              <div className="flex-1 mx-3 text-center">
                <h1 className="text-white text-base font-bold truncate bg-gradient-to-r from-orange-400 via-white to-orange-400 bg-clip-text text-transparent drop-shadow-lg">
                  {project.title}
                </h1>
              </div>
              <div className="w-11"></div>
            </div>

            <div className="h-full overflow-y-auto pt-16 pb-24" onClick={(e) => e.stopPropagation()}>
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={project.id}
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                >
                  <div className="w-full min-h-[50vh] flex items-center justify-center bg-gradient-to-br from-gray-900 to-black p-4">
                    <ProjectMediaViewer project={project} />
                  </div>

                  <div className="p-6 bg-gradient-to-b from-black to-gray-950">
                    <div className="mb-6">
                      <h2 className="text-2xl font-bold text-white mb-3 bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">
                        {project.title}
                      </h2>
                      <p className="text-gray-300 text-sm leading-relaxed">{project.fullDescription}</p>
                    </div>

                    <div className="mb-6">
                      <h3 className="text-white font-semibold text-xs mb-3 flex items-center gap-2 uppercase">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-orange-400">
                          <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
                        </svg>
                        Technologies
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {project.tech.map((tech, i) => (
                          <span key={i} className="px-2.5 py-1 bg-orange-500/20 text-orange-300 text-xs rounded-full border border-orange-500/30">
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="mb-6">
                      <h3 className="text-white font-semibold text-xs mb-3 flex items-center gap-2 uppercase">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-orange-400">
                          <path d="M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z"/>
                        </svg>
                        Fonctionnalités
                      </h3>
                      <div className="space-y-2">
                        {project.features.map((feat, i) => (
                          <div key={i} className="flex items-start gap-2 text-gray-300 text-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-orange-400 mt-0.5">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                            <span>{feat}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-col gap-3">
                      {project.github && (
                        <a href={project.github} target="_blank" rel="noopener noreferrer" className="w-full px-4 py-3 bg-white/10 text-white rounded-xl border border-white/20 flex items-center justify-center gap-2 text-sm">
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4"/>
                          </svg>
                          Code source
                        </a>
                      )}
                      {project.demo && (
                        <a href={project.demo} target="_blank" rel="noopener noreferrer" className="w-full px-4 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl flex items-center justify-center gap-2 text-sm">
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20.2 6 3 11l-.9-2.4c-.3-1.1.3-2.2 1.3-2.5l13.5-4c1.1-.3 2.2.3 2.5 1.3Z"/>
                          </svg>
                          Voir la démo
                        </a>
                      )}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-t from-black via-black/95 to-transparent p-4 pb-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex gap-3">
                <motion.button 
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePrevious();
                  }} 
                  className="flex-1 px-4 py-3.5 bg-gradient-to-r from-white/5 to-white/10 backdrop-blur-sm text-white rounded-2xl border border-white/20 flex items-center justify-center gap-2 text-sm font-medium shadow-lg shadow-black/30"
                  whileTap={{ scale: 0.95 }}
                  whileHover={{ scale: 1.02 }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="m15 18-6-6 6-6"/>
                  </svg>
                  Précédent
                </motion.button>
                <motion.button 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNext();
                  }} 
                  className="flex-1 px-4 py-3.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-2xl border border-orange-400/50 flex items-center justify-center gap-2 text-sm font-medium shadow-lg shadow-orange-500/40"
                  whileTap={{ scale: 0.95 }}
                  whileHover={{ scale: 1.02 }}
                >
                  Suivant
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="m9 18 6-6-6-6"/>
                  </svg>
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
